import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { ImageTile } from './ImageTile';
import { TextTile } from './TextTile';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useMemo } from 'react';

type Props<RelatedTerm extends Record<string, string>> = {
    containerClasses?: string;
    heading?: string;
    headingMessageId?: string;
    imageKey?: keyof RelatedTerm;
    labelKey: keyof RelatedTerm;
    relatedTerms: RelatedTerm[];
    urlKey: keyof RelatedTerm;
};

export const RelatedSearches = <RelatedTerm extends Record<string, string>>({
    containerClasses,
    heading,
    headingMessageId,
    imageKey,
    labelKey,
    relatedTerms,
    urlKey,
}: Props<RelatedTerm>) => {
    const { formatMessage } = useIntl();

    const headingText = useMemo(() => {
        if (heading) {
            return heading;
        }
        if (headingMessageId) {
            return formatMessage({
                id: headingMessageId,
            });
        }
        return '';
    }, [heading, headingMessageId, formatMessage]);

    if (relatedTerms.length === 0) {
        return null;
    }

    return (
        <div className={`flex flex-col gap-sm ${containerClasses || ''}`}>
            {headingText && <H2>{headingText}</H2>}
            <div className="flex flex-wrap items-stretch gap-xs">
                {relatedTerms.map((term) => {
                    const label = term[labelKey];
                    const url = term[urlKey];
                    const imageUrl = imageKey ? term[imageKey] : '';

                    if (label === '' || url === '') {
                        return null;
                    }

                    return imageKey && imageUrl ? (
                        <ImageTile
                            imageUrl={imageUrl}
                            key={label}
                            label={label}
                            url={url}
                        />
                    ) : (
                        <TextTile
                            key={label}
                            label={label}
                            url={url}
                        />
                    );
                })}
            </div>
        </div>
    );
};
